import React, { Suspense, lazy, useEffect } from 'react';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import MarkdownPage from './components/Legal/MarkdownPage';
// import { useTranslation } from 'react-i18next';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
// import Sidebar from './components/Layout/Sidebar';
import Home from './pages/Home/Home';
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Contact from './pages/Contact/Contact';
import Signup from './pages/Auth/Signup';
import Pricing from './pages/Pricing/Pricing';
// import Account from './pages/Account/Account';
// import Contact from './pages/Contact/Contact';
// import Pricing from './pages/Pricing/Pricing';
import TextTools from './pages/TextTools/TextTools';
import ImageTools from './pages/ImageTools/ImageTools';
import VideoTools from './pages/VideoTools/VideoTools';
import AudioTools from './pages/AudioTools/AudioTools';
import Services from './pages/Services/Services';
import LitBotPage from './pages/LitBot/LitBotPage'; 
// import TutorialsPage from './pages/Tutorials/TutorialsPage';
import ChatPage from './pages/Chat/ChatPage'; // Import the new page
import ToolRenderer from './pages/ToolRenderer';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import AccountPage from './pages/Account/AccountPage';
import './utils/i18n';
import './index.css';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import PayPalPage from './pages/Pricing/PayPalPage';
import PurchaseCredits from './pages/Pricing/PurchaseCredits'; // Import
import PaymentSuccess from './pages/Pricing/PaymentSuccess'; // Import
import PaymentCancel from './pages/Pricing/PaymentCancel';  
import NotFound from './pages/NotFound';
import ArticleGenerator from './pages/TextTools/ArticleGenerator';
import ImageEditor from './pages/ImageTools/ImageEditor';


const initialOptions = {
    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
    components: "buttons",
    currency: "EUR", // Set a global currency for consistency
    // REMOVE intent and vault from here
};


// Loading component
const LoadingSpinner = () => (
	<div className="min-h-screen bg-brand-dark flex items-center justify-center">
		<div className="text-white text-xl">Loading...</div>
	</div>
);

// Tool Page Component (placeholder)
const ToolPage = () => {
	const location = useLocation();
	const { toolSlug } = useParams();
	return (
		<div className="container mx-auto px-4 py-8">
			<h1 className="text-white text-3xl">Tool: {toolSlug}</h1>
		</div>
	);
};

const AppContent = () => {
    const location = useLocation();
    useEffect(() => {
    if (window.gtag) {
        window.gtag("event", "page_view", { page_path: location.pathname });
    }
}, [location]);
    // 2. Define all paths where you want to HIDE the footer
    const pathsWithoutFooter = [
        '/chat',
        '/tools',
        '/tools/Edit-Image' ,
        '/litbot',
        '/article-generator',
        '/image-generation'

    ];

    // 3. Check if the current path starts with any of the excluded paths
    const shouldShowFooter = !pathsWithoutFooter.some(path => location.pathname.startsWith(path));

    return (
        <div className="App flex flex-col h-screen bg-brand-dark text-white overflow-hidden">
            <Navbar />
            <main className="flex-1 flex flex-col overflow-y-auto">
                <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
		    <Route path="/forgot-password" element={<ForgotPassword />} />
                    {/* Matches the link sent in the email */}
                    <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
                    <Route path="/signup" element={<Signup />} />
		    <Route path="/contact" element={<Contact />} />
                    <Route path="/terms" element={<MarkdownPage docName="terms" />} />
                    <Route path="/privacy" element={<MarkdownPage docName="privacy" />} />
                    <Route path="/impressum" element={<MarkdownPage docName="impressum" />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/litbot" element={<ProtectedRoute><LitBotPage /></ProtectedRoute>} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/paypal" element={<PayPalPage />} />

                    <Route path="/purchase-credits" element={<ProtectedRoute><PurchaseCredits /></ProtectedRoute>} />
                    <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                    <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
                    <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />


                    {/* Tool Pages */}
                    <Route path="/text-tools" element={<TextTools />} />
                    <Route path="/image-tools" element={<ImageTools />} />
                    <Route path="/video-tools" element={<VideoTools />} />
                    <Route path="/audio-tools" element={<AudioTools />} />

                    <Route path="/article-generator" element={<ProtectedRoute><ArticleGenerator /></ProtectedRoute>} />
                    <Route path="/article-generator/:projectId" element={<ProtectedRoute><ArticleGenerator /></ProtectedRoute>} />

                    <Route path="/tools/Edit-Image" element={<ProtectedRoute><ImageEditor /></ProtectedRoute>} />
                    


                    {/* Routes that should NOT have a footer */}
                    <Route path="/tools/:toolSlug/:projectId?" element={<ProtectedRoute><ToolRenderer /></ProtectedRoute>} />
                    


                    <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
                    <Route path="/chat/:chatId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />

                    {/* 404 Page */}
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </main>
            {/* 4. Conditionally render the Footer based on the logic above */}
            {shouldShowFooter && <Footer />}
        </div>
    );
};


function App() {
	
	return (
        <PayPalScriptProvider options={initialOptions}>
		<Router>
			<Suspense fallback={<LoadingSpinner />}>
				<AuthProvider>
					<LanguageProvider>
						<AppContent />
					</LanguageProvider>
				</AuthProvider>
			</Suspense>
		</Router>
        </PayPalScriptProvider>
	);
}




export default App;
